import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/services/firebase';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { ArrowLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

interface WireTransferForm {
  fromAccount: string;
  toAccount: string;
  toRoutingNumber: string;
  amount: string;
  currency: string;
  senderLocation: 'in-person' | 'remote';
  email?: string;
}

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'EUR', name: 'Euro', flag: '🇪🇺' },
  { code: 'GBP', name: 'British Pound', flag: '🇬🇧' },
  { code: 'CAD', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'JPY', name: 'Japanese Yen', flag: '🇯🇵' },
  { code: 'CHF', name: 'Swiss Franc', flag: '🇨🇭' },
  { code: 'HKD', name: 'Hong Kong Dollar', flag: '🇭🇰' },
];

// Basic routing number validation (9 digits)
const validateRoutingNumber = (routingNumber: string): boolean => {
  return /^\d{9}$/.test(routingNumber);
};

// Mock bank lookup function (in a real app, you'd use a proper API)
const lookupBankName = async (routingNumber: string): Promise<string | null> => {
  // This is a mock implementation - in reality you'd call a bank lookup API
  // For demo purposes, we'll return some mock bank names based on routing number patterns
  if (routingNumber.startsWith('021')) return 'Chase Bank';
  if (routingNumber.startsWith('011')) return 'Bank of America';
  if (routingNumber.startsWith('026')) return 'Wells Fargo';
  if (routingNumber.startsWith('006')) return 'Bank One';
  return 'Investar Bank of Louisiana';
};

export default function AddWireTransferPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<WireTransferForm>({
    fromAccount: '',
    toAccount: '',
    toRoutingNumber: '',
    amount: '',
    currency: 'USD',
    senderLocation: 'in-person',
    email: '',
  });
  const [bankName, setBankName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<WireTransferForm>>({});

  // Prefill fromAccount from query param if present
  useEffect(() => {
    const fromAccount = searchParams.get('fromAccount');
    if (fromAccount) {
      setFormData((prev) => ({ ...prev, fromAccount }));
    }
  }, [searchParams]);

  const handleInputChange = (field: keyof WireTransferForm, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }

    // Handle routing number lookup
    if (field === 'toRoutingNumber') {
      if (validateRoutingNumber(value)) {
        lookupBankName(value).then(name => {
          setBankName(name || '');
        });
      } else {
        setBankName('');
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<WireTransferForm> = {};

    if (!formData.fromAccount.trim()) {
      newErrors.fromAccount = 'From account is required';
    }

    if (!formData.toAccount.trim()) {
      newErrors.toAccount = 'To account is required';
    }

    if (!formData.toRoutingNumber.trim()) {
      newErrors.toRoutingNumber = 'Routing number is required';
    } else if (!validateRoutingNumber(formData.toRoutingNumber)) {
      newErrors.toRoutingNumber = 'Routing number must be 9 digits';
    }

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = 'Amount must be a positive number';
      }
    }

    // If remote, require a valid email
    if (formData.senderLocation === 'remote') {
      const email = (formData.email || '').trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email) {
        newErrors.email = 'Email is required for remote senders';
      } else if (!emailRegex.test(email)) {
        newErrors.email = 'Enter a valid email address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const wireTransferData = {
        fromAccount: formData.fromAccount.trim(),
        toAccount: formData.toAccount.trim(),
        toRoutingNumber: formData.toRoutingNumber.trim(),
        bankCode: 'b1bank', // Default as requested
        requestedBy: 'Mary Barker Clark', // Default as requested
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        senderLocation: formData.senderLocation,
        // Include email for remote verification trigger
        ...(formData.senderLocation === 'remote' && formData.email ? { email: formData.email.trim() } : {}),
        isManagerConfirmed: false,
        isRecipientConfirmed: false,
        isSenderConfirmed: false,
        isSent: false,
        initiatedDate: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'wires'), wireTransferData);
      console.log('Wire transfer created with ID:', docRef.id);

      // Navigate to wire summary page
      navigate(`/wire-transfers/summary/${docRef.id}`);
    } catch (error) {
      console.error('Error creating wire transfer:', error);
      alert('Failed to create wire transfer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/wire-transfers');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="py-6 container-centered">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Wire Transfers
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Add New Wire Transfer</h1>
              <p className="text-gray-600">Create a new wire transfer request</p>
            </div>
          </div>
        </div>
      </div>

      <div className="py-8 container-centered">
        <div className="max-w-2xl mx-auto">
          <div className="p-8 bg-white border border-gray-200 rounded-lg shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* From Account */}
              <div>
                <Input
                  label="From Account"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter source account number"
                  value={formData.fromAccount}
                  onChange={(e) => handleInputChange('fromAccount', e.target.value)}
                  error={errors.fromAccount}
                  required
                />
              </div>

              {/* To Account */}
              <div>
                <Input
                  label="To Account"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter destination account number"
                  value={formData.toAccount}
                  onChange={(e) => handleInputChange('toAccount', e.target.value)}
                  error={errors.toAccount}
                  required
                />
              </div>

              {/* Routing Number */}
              <div>
                <Input
                  label="Routing Number"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter 9-digit routing number"
                  value={formData.toRoutingNumber}
                  onChange={(e) => handleInputChange('toRoutingNumber', e.target.value)}
                  error={errors.toRoutingNumber}
                  maxLength={9}
                  required
                />
                {bankName && (
                  <div className="mt-2 text-sm text-green-600">
                    ✓ Bank: {bankName}
                  </div>
                )}
              </div>

              {/* Amount and Currency */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <Input
                    label="Amount"
                    type="number"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    error={errors.amount}
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {CURRENCIES.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.flag} {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Sender Location */}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Sender Location
                </label>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className={`relative flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.senderLocation === 'in-person'
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}>
                    <input
                      type="radio"
                      name="senderLocation"
                      value="in-person"
                      checked={formData.senderLocation === 'in-person'}
                      onChange={(e) => handleInputChange('senderLocation', e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                        formData.senderLocation === 'in-person'
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {formData.senderLocation === 'in-person' && (
                          <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">In-Person</div>
                        <div className="text-sm text-gray-600">Sender is physically present</div>
                      </div>
                    </div>
                  </label>
                  
                  <label className={`relative flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.senderLocation === 'remote'
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}>
                    <input
                      type="radio"
                      name="senderLocation"
                      value="remote"
                      checked={formData.senderLocation === 'remote'}
                      onChange={(e) => handleInputChange('senderLocation', e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                        formData.senderLocation === 'remote'
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {formData.senderLocation === 'remote' && (
                          <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Remote</div>
                        <div className="text-sm text-gray-600">Sender is not physically present</div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Email (only for remote) */}
              {formData.senderLocation === 'remote' && (
                <div>
                  <Input
                    label="Sender Email"
                    type="email"
                    placeholder="name@example.com"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={errors.email}
                    required
                  />
                </div>
              )}

              {/* Requested By (Read-only) */}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Requested By
                </label>
                <div className="w-full px-3 py-2 text-gray-600 border border-gray-300 rounded-md bg-gray-50">
                  Mary Barker Clark
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  This field is automatically set
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end pt-6 space-x-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="text-white bg-blue-600 hover:bg-blue-700"
                >
                  {isSubmitting ? (
                    'Creating...'
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                      Create Wire Transfer
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
